import React, { useState } from 'react';
import { Megaphone, Bell, X, AlertTriangle, CheckCircle, Info, Shield, ShieldOff, Ban, UserX, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminUsers = ({ users, announcements, loadData }) => {
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [notifyUserId, setNotifyUserId] = useState(null);
  const [notifyUserName, setNotifyUserName] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyType, setNotifyType] = useState('general');

  const handleBanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to ban/unban this user?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/users/${userId}/ban`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User ban status updated');
      loadData();
    } catch (error) { toast.error('Failed to update user'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User deleted');
      loadData();
    } catch (error) { toast.error('Failed to delete user'); }
  };

  const handleToggleAdmin = async (userId, isCurrentlyAdmin) => {
    if (!window.confirm(`Are you sure you want to ${isCurrentlyAdmin ? 'remove admin from' : 'make admin'} this user?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/users/${userId}/toggle-admin`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isCurrentlyAdmin ? 'Admin removed' : 'User promoted to admin');
      loadData();
    } catch (error) { toast.error('Failed to update user'); }
  };

  const handleSendAnnouncement = async () => {
    if (!announcement.trim()) { toast.error('Please enter an announcement message'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/announcements`, { title: announcementTitle, message: announcement }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Announcement posted to forum!');
      setShowAnnouncementModal(false);
      setAnnouncement('');
      setAnnouncementTitle('');
      loadData();
    } catch (error) { toast.error('Failed to post announcement'); }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/announcements/${announcementId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Announcement deleted');
      loadData();
    } catch (error) { toast.error('Failed to delete announcement'); }
  };

  const handleSendNotification = async () => {
    if (!notifyMessage.trim()) { toast.error('Please enter a message'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/users/${notifyUserId}/notify`, { message: notifyMessage, type: notifyType }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Notification sent to ${notifyUserName}`);
      setShowNotifyModal(false);
      setNotifyMessage('');
      setNotifyUserId(null);
      setNotifyUserName('');
    } catch (error) { toast.error('Failed to send notification'); }
  };

  return (
    <div>
      <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">All Users</h2>
          <Button onClick={() => setShowAnnouncementModal(true)} className="bg-[#8B1818] text-white hover:bg-[#701313]">
            <Megaphone className="h-4 w-4 mr-2" />Send Announcement
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Name</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Email</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Role</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Joined</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4 text-slate-900">{u.name}</td>
                  <td className="py-3 px-4 text-slate-600">{u.email}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${u.is_admin ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>{u.is_admin ? 'Admin' : 'User'}</span>
                      {u.is_banned && <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800">Banned</span>}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-600 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button onClick={() => { setNotifyUserId(u.id); setNotifyUserName(u.name); setShowNotifyModal(true); }} className="p-2 hover:bg-slate-100 rounded" title="Send Notification"><Bell className="h-4 w-4 text-blue-600" /></button>
                      <button onClick={() => handleToggleAdmin(u.id, u.is_admin)} className="p-2 hover:bg-slate-100 rounded" title={u.is_admin ? 'Remove Admin' : 'Make Admin'}>{u.is_admin ? <ShieldOff className="h-4 w-4 text-orange-600" /> : <Shield className="h-4 w-4 text-green-600" />}</button>
                      <button onClick={() => handleBanUser(u.id)} className={`p-2 hover:bg-slate-100 rounded ${u.is_banned ? 'bg-yellow-100' : ''}`} title={u.is_banned ? 'Unban User' : 'Ban User'}><Ban className={`h-4 w-4 ${u.is_banned ? 'text-green-600' : 'text-yellow-600'}`} /></button>
                      <button onClick={() => handleDeleteUser(u.id)} className="p-2 hover:bg-slate-100 rounded" title="Delete User"><UserX className="h-4 w-4 text-red-600" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAnnouncementModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-slate-900">Create Forum Announcement</h3>
              <button onClick={() => setShowAnnouncementModal(false)}><X className="h-6 w-6 text-slate-500" /></button>
            </div>
            <p className="text-slate-600 mb-4">This announcement will be posted in the Admin Announcements section of the forum.</p>
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={announcementTitle} onChange={(e) => setAnnouncementTitle(e.target.value)} placeholder="Announcement title..." className="mt-2" /></div>
              <div><Label>Message</Label><Textarea value={announcement} onChange={(e) => setAnnouncement(e.target.value)} placeholder="Enter your announcement message..." rows={6} className="mt-2" /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleSendAnnouncement} className="bg-[#8B1818] text-white hover:bg-[#701313]"><Megaphone className="h-4 w-4 mr-2" />Post Announcement</Button>
              <Button onClick={() => setShowAnnouncementModal(false)} variant="outline">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-slate-900">Send Notification</h3>
              <button onClick={() => setShowNotifyModal(false)}><X className="h-6 w-6 text-slate-500" /></button>
            </div>
            <p className="text-slate-600 mb-4">Send a notification to <strong>{notifyUserName}</strong></p>
            <div className="space-y-4">
              <div><Label>Notification Type</Label>
                <select value={notifyType} onChange={(e) => setNotifyType(e.target.value)} className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg">
                  <option value="general">General</option><option value="warning">Warning</option><option value="approval">Approval</option><option value="info">Information</option>
                </select></div>
              <div><Label>Message</Label><Textarea value={notifyMessage} onChange={(e) => setNotifyMessage(e.target.value)} placeholder="Enter your notification message..." rows={4} className="mt-2" /></div>
              <div className={`p-3 rounded-lg flex items-start gap-3 ${notifyType === 'warning' ? 'bg-yellow-50 border border-yellow-200' : notifyType === 'approval' ? 'bg-green-50 border border-green-200' : notifyType === 'info' ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 border border-slate-200'}`}>
                {notifyType === 'warning' && <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />}
                {notifyType === 'approval' && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />}
                {notifyType === 'info' && <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                {notifyType === 'general' && <Bell className="h-5 w-5 text-slate-600 flex-shrink-0" />}
                <div className="text-sm"><p className="font-medium text-slate-900">Preview</p><p className="text-slate-600">{notifyMessage || 'Your message will appear here...'}</p></div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={handleSendNotification} className="bg-[#8B1818] text-white hover:bg-[#701313]"><Bell className="h-4 w-4 mr-2" />Send Notification</Button>
              <Button onClick={() => setShowNotifyModal(false)} variant="outline">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {announcements.length > 0 && (
        <div className="bg-white rounded-xl p-8 shadow-sm border border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Forum Announcements</h2>
          <div className="space-y-4">
            {announcements.map((ann) => (
              <div key={ann.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900">{ann.title || 'Announcement'}</h3>
                    <p className="text-slate-600 mt-1">{ann.message}</p>
                    <p className="text-xs text-slate-500 mt-2">Posted by {ann.author_name} - {new Date(ann.created_at).toLocaleDateString()}</p>
                  </div>
                  <button onClick={() => handleDeleteAnnouncement(ann.id)} className="text-red-500 hover:text-red-700 ml-4"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
